import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { DNA_PROFILES, DESTINATIONS } from '@/data/destinations'

type ExportType = 'card' | 'itinerary' | 'phone' | 'share'

const TIMES = [['9:00am', '1:00pm', '4:30pm'], ['8:30am', '12:00pm', '3:00pm', '7:00pm'], ['10:00am', '2:00pm', '5:30pm']]

export function JourneyCardScreen() {
  const { selectedDestination, dnaKey, setScreen, resetJourney } = useAppStore()
  const [activeExport, setActiveExport] = useState<ExportType>('card')
  const [phoneMode, setPhoneMode] = useState<'sms' | 'whatsapp'>('sms')
  const [phoneSent, setPhoneSent] = useState(false)
  const [copied, setCopied] = useState(false)

  const profile = DNA_PROFILES[dnaKey]
  const dest = selectedDestination ?? (DESTINATIONS[dnaKey]?.[0] || null)
  if (!dest || !profile) { setScreen('journey'); return null }

  const exports = [
    { type: 'card' as ExportType, icon: '✦', title: 'Journey Card', desc: 'Your DNA, destinations & partner savings' },
    { type: 'itinerary' as ExportType, icon: '☰', title: 'Full Itinerary', desc: 'Complete day-by-day plan' },
    { type: 'phone' as ExportType, icon: '📱', title: 'Send to Phone', desc: 'Get it via SMS or WhatsApp' },
    { type: 'share' as ExportType, icon: '🔗', title: 'Share Link', desc: 'Share with travel companions' },
  ] as const

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: 'radial-gradient(ellipse at 50% 0%, #33200F 0%, #241209 55%, #180C05 100%)',
      position: 'relative',
    }}>
      <Atmosphere count={16} />
      <div style={{ position: 'relative', zIndex: 5 }}>
        <Topbar center={`${dest.name} · Journey Card`} backTo="journey" backLabel="← Itinerary" />
      </div>

      <div style={{ padding: 16, flex: 1, position: 'relative', zIndex: 5 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 14, alignItems: 'start' }}>

          {/* Journey Card Visual */}
          <div className="p-glow-card" style={{ border: '1px solid rgba(212,160,23,0.25)', background: 'rgba(255,255,255,0.03)', overflow: 'hidden' }}>
            {/* Hero */}
            <div style={{ height: 120, background: dest.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, position: 'relative' }}>
              {dest.emoji}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 40%, rgba(24,12,5,0.75) 100%)' }} />
              <div style={{ position: 'absolute', top: 9, left: 11, fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase' }}>{dest.flag}</div>
              <div style={{ position: 'absolute', bottom: 9, left: 11, fontFamily: 'var(--serif-display)', fontSize: 22, color: '#fff', fontWeight: 600 }}>{dest.name}</div>
              <div style={{ position: 'absolute', bottom: 9, right: 11, fontFamily: 'var(--serif-body)', fontSize: 10, color: 'rgba(255,255,255,0.65)', fontStyle: 'italic' }}>{dest.theme}</div>
            </div>

            <div style={{ padding: 14 }}>
              {/* DNA row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ color: 'var(--gold2)', fontSize: 16 }}>✦</span>
                <div>
                  <div style={{ fontFamily: 'var(--serif-display)', fontSize: 14, fontStyle: 'italic', color: '#F5EDD8' }}>{profile.name}</div>
                  <div style={{ fontFamily: 'var(--sans)', fontSize: 10, color: 'rgba(245,237,216,0.55)' }}>Your travel DNA · Passage by Imaginarium</div>
                </div>
              </div>

              {/* Days */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {dest.stops.slice(0, 3).map((stop, i) => (
                  <div key={i} style={{ borderTop: '1px solid rgba(212,160,23,0.18)', paddingTop: 8 }}>
                    <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', marginBottom: 3 }}>
                      {stop.time}
                    </div>
                    <div style={{ fontFamily: 'var(--serif-display)', fontSize: 13, color: '#F5EDD8' }}>{stop.name}</div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                      {(TIMES[i] || TIMES[0]).map((t, ti) => (
                        <span key={ti} style={{ fontFamily: 'var(--sans)', fontSize: 9, padding: '2px 6px', background: 'rgba(212,160,23,0.12)', color: 'rgba(245,237,216,0.72)' }}>⏱ {t}</span>
                      ))}
                    </div>
                    <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'var(--gold2)', letterSpacing: '0.06em', marginTop: 4, textTransform: 'uppercase' }}>{stop.curatorNote}</div>
                  </div>
                ))}
              </div>

              {/* Savings */}
              <div style={{ marginTop: 12, padding: 10, background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.3)', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--serif-body)', fontSize: 12, color: 'var(--gold2)' }}>
                <span>✦</span><span>Save {dest.saving} · Travelpayouts partner offers via Passage</span>
              </div>

              {/* QR */}
              <div style={{ width: 40, height: 40, background: '#241209', margin: '12px auto', display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 1, padding: 3, borderRadius: 2, border: '1px solid rgba(212,160,23,0.3)' }}>
                {[1,0,1,1,0,0,1,0,1,1,0,1,1,0,1,0,0,1,1,0,1,1,0,0,1].map((v, i) => (
                  <div key={i} style={{ background: v ? 'var(--parch)' : '#241209', borderRadius: 1 }} />
                ))}
              </div>

              <div style={{ textAlign: 'center', fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'rgba(245,237,216,0.45)', letterSpacing: '0.1em', textTransform: 'uppercase', borderTop: '1px solid rgba(212,160,23,0.18)', paddingTop: 8 }}>
                Passage by Imaginarium · passage.imaginarium.club
              </div>
            </div>
          </div>

          {/* Export panel */}
          <div>
            <div style={{ fontFamily: 'var(--serif-display)', fontSize: 15, color: '#F5EDD8', marginBottom: 12, fontStyle: 'italic' }}>✦ Your Journey Card</div>

            {exports.map((item) => (
              <div key={item.type}>
                <button onClick={() => {
                  if (item.type === 'itinerary') { setScreen('itinerary'); return }
                  setActiveExport(item.type); setPhoneSent(false); setCopied(false)
                }}
                  style={{
                    width: '100%', textAlign: 'left', border: activeExport === item.type ? '2px solid var(--gold)' : '1px solid rgba(212,160,23,0.2)',
                    padding: 11, marginBottom: 8, background: activeExport === item.type ? 'rgba(212,160,23,0.1)' : 'rgba(255,255,255,0.03)', cursor: 'pointer',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, fontFamily: 'var(--serif-display)', fontSize: 13, color: '#F5EDD8' }}>
                    <span style={{ color: 'var(--gold2)' }}>{item.icon}</span> {item.title}
                  </div>
                  <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'rgba(245,237,216,0.58)', lineHeight: 1.4 }}>{item.desc}</div>
                </button>

                {/* Journey Card focus state — the preview is already shown alongside; give a short confirmation note */}
                {item.type === 'card' && activeExport === 'card' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} style={{ marginBottom: 8, padding: '0 2px' }}>
                    <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11.5, color: 'rgba(245,237,216,0.6)', fontStyle: 'italic', lineHeight: 1.6 }}>
                      Your Journey Card is shown to the left — your DNA, your destinations, and every partner saving unlocked for this trip.
                    </div>
                  </motion.div>
                )}

                {/* Share link focus state */}
                {item.type === 'share' && activeExport === 'share' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} style={{ marginBottom: 8, padding: '0 2px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input readOnly value={`https://passage.imaginarium.club/j/${dest.id}`} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(212,160,23,0.2)', padding: '8px 10px', fontFamily: 'var(--serif-body)', fontSize: 11, color: '#F5EDD8' }} />
                      <button onClick={() => {
                        const link = `https://passage.imaginarium.club/j/${dest.id}`
                        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(link).catch(() => {})
                        setCopied(true)
                      }} style={{ padding: '8px 12px', background: 'linear-gradient(90deg, var(--gold), var(--gold2))', color: '#241209', fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.08em', border: 'none', cursor: 'pointer', textTransform: 'uppercase' }}>
                        {copied ? 'Copied ✓' : 'Copy'}
                      </button>
                    </div>
                    <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'rgba(245,237,216,0.55)', fontStyle: 'italic', marginTop: 6 }}>
                      Share this journey with travel companions — they'll see the same curated plan and partner offers.
                    </div>
                  </motion.div>
                )}

                {/* Phone panel */}
                {item.type === 'phone' && activeExport === 'phone' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} style={{ marginBottom: 8, padding: '0 2px' }}>
                    {!phoneSent ? (
                      <>
                        <div style={{ display: 'flex', gap: 5, marginBottom: 6 }}>
                          {(['sms', 'whatsapp'] as const).map((m) => (
                            <button key={m} onClick={() => setPhoneMode(m)} style={{
                              flex: 1, padding: '6px', fontSize: 11, fontFamily: 'var(--serif-caps)', letterSpacing: '0.06em',
                              border: phoneMode === m ? '1px solid var(--gold)' : '1px solid rgba(212,160,23,0.2)',
                              background: phoneMode === m ? 'rgba(212,160,23,0.1)' : 'rgba(255,255,255,0.03)',
                              color: phoneMode === m ? 'var(--gold2)' : 'rgba(245,237,216,0.6)', cursor: 'pointer',
                            }}>
                              {m === 'sms' ? '📱 SMS' : '💬 WhatsApp'}
                            </button>
                          ))}
                        </div>
                        <select style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(212,160,23,0.2)', padding: '7px 9px', fontFamily: 'var(--serif-body)', fontSize: 11, color: '#F5EDD8', marginBottom: 5 }}>
                          <option>GB +44 United Kingdom</option>
                          <option>US +1 United States</option>
                          <option>AU +61 Australia</option>
                          <option>IE +353 Ireland</option>
                          <option>ZA +27 South Africa</option>
                        </select>
                        <input placeholder="Your number…" style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(212,160,23,0.2)', padding: '8px 10px', fontFamily: 'var(--serif-body)', fontSize: 12, color: '#F5EDD8', marginBottom: 6 }} />
                        <button onClick={() => setPhoneSent(true)} style={{ width: '100%', padding: 9, background: 'linear-gradient(90deg, var(--gold), var(--gold2))', color: '#241209', fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.12em', border: 'none', cursor: 'pointer', textTransform: 'uppercase' }}>
                          Send now
                        </button>
                      </>
                    ) : (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: 'center', padding: 12 }}>
                        <div style={{ fontFamily: 'var(--serif-display)', fontSize: 16, color: 'var(--gold2)', marginBottom: 5, fontStyle: 'italic' }}>Sent ✓</div>
                        <div style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: 'rgba(245,237,216,0.62)', fontStyle: 'italic' }}>
                          On its way. Your journey and all partner offers await you.
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </div>
            ))}

            <button onClick={() => window.print()} style={{
              width: '100%', padding: 12, background: 'linear-gradient(90deg, var(--gold), var(--gold2))', border: 'none',
              color: '#241209', fontFamily: 'var(--serif-caps)', fontSize: 11,
              letterSpacing: '0.12em', cursor: 'pointer', textTransform: 'uppercase', marginBottom: 8,
            }}>✦ Print Journey Card</button>

            <button onClick={resetJourney} style={{
              width: '100%', padding: 10, background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(212,160,23,0.2)', color: 'rgba(245,237,216,0.6)',
              fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.1em',
              cursor: 'pointer', textTransform: 'uppercase',
            }}>+ Begin a new journey</button>
          </div>
        </div>
      </div>
    </div>
  )
}
